import { Modal } from "@/components/elements/Modal";
import { FCC } from "@/types/common";
import { Box, CircularProgress, Stack, SxProps, Typography, Link } from "@mui/material";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { orange, secondary, text } from "@/theme/colors";
import {
  TAG_DELETE_CONFIRM_MESSAGE,
  TAG_DELETE_IN_USE_MESSAGE,
  TAG_DELETE_NEWLY_ADDED_MESSAGE,
  TAG_DELETE_SIMPLE_CONFIRM_MESSAGE,
  TAG_DELETE_UNASSIGN_MESSAGE,
} from "@/features/job/constants/common";
import { TDeleteTagOutcome, TTagEntityType, TTagUsingJob, validateDeleteTagApi } from "@/api/services/main/tag";
import { showError } from "@/utils/error";
import { UIEvent, useEffect, useRef, useState } from "react";

const PER_PAGE = 100;
// Each list (validate + re-validate) caps at ~5 rows, then scrolls — keeps the modal within the viewport.
const LIST_MAX_HEIGHT = 170;

const JOB_LINK_SX: SxProps = {
  textDecoration: "none",
  color: secondary[80],
  cursor: "pointer",
  textAlign: "left",
  wordWrap: "break-word",
  whiteSpace: "normal",
  display: "inline-block",
  width: "90%",
  "&:hover": { textDecoration: "underline" },
};

// Scrolled within 1.5 viewport-heights of the bottom — the trigger point for infinite-scroll paging.
const isNearBottom = (e: UIEvent<HTMLDivElement>) => {
  const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
  return scrollHeight - scrollTop <= clientHeight * 1.5;
};

type TJobLinkItemProps = {
  job: TTagUsingJob;
  onClick: (jobId: number) => void;
};

const JobLinkItem = ({ job, onClick }: TJobLinkItemProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography component="span" sx={{ color: text.primary, lineHeight: 1, flexShrink: 0 }}>
      ・
    </Typography>
    <Link component="button" variant="body1" onClick={() => onClick(job.id)} sx={JOB_LINK_SX}>
      {job.jobName}
    </Link>
  </Box>
);

type TJobScrollListProps = {
  jobs: TTagUsingJob[];
  loading: boolean;
  onScroll: (e: UIEvent<HTMLDivElement>) => void;
  onJobClick: (jobId: number) => void;
};

const JobScrollList = ({ jobs, loading, onScroll, onJobClick }: TJobScrollListProps) => (
  <Stack spacing={1.5} onScroll={onScroll} sx={{ maxHeight: LIST_MAX_HEIGHT, overflow: "auto" }}>
    {jobs.map((job) => (
      <JobLinkItem key={job.id} job={job} onClick={onJobClick} />
    ))}
    {loading && (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    )}
  </Stack>
);

type TConfirmDeleteTagModalProps = {
  open: boolean;
  onClose: () => void;
  // Performs the delete with the given cutoff; returns the outcome (success or conflict with newly-tagged jobs).
  onOk: (validatedAt: string) => Promise<TDeleteTagOutcome>;
  isLoading?: boolean;
  entityType: TTagEntityType;
  tagId: number;
  tagLabel: string;
  initialJobs: TTagUsingJob[];
  totalCount: number;
  initialValidatedAt: string;
};

/**
 * Confirm-delete dialog for a tag, with optimistic-concurrency handling.
 * - Top list: jobs using the tag at dialog-open time (frozen snapshot via `until`). Read-only, paginated.
 * - Confirm: tries to delete with the current `cutoff`. If another user tagged jobs since, the BE returns those jobs
 *   instead of deleting → they appear in the bottom list, `cutoff` advances, user re-confirms until nothing new remains.
 * - Bottom list: the "newly added since `cutoff`" jobs (paginated via `since`).
 */
export const ConfirmDeleteTagModal: FCC<TConfirmDeleteTagModalProps> = ({
  open,
  onClose,
  onOk,
  isLoading,
  entityType,
  tagId,
  tagLabel,
  initialJobs,
  totalCount,
  initialValidatedAt,
}) => {
  const [jobs, setJobs] = useState<TTagUsingJob[]>(initialJobs);
  const [total, setTotal] = useState<number>(totalCount);
  const [reachedEnd, setReachedEnd] = useState<boolean>(initialJobs.length < PER_PAGE);
  const [loading, setLoading] = useState(false);
  // Cutoff for the NEXT confirm attempt. Advances after each conflict so the delete loop converges.
  const [cutoff, setCutoff] = useState(initialValidatedAt);

  // Bottom section: jobs tagged after the cutoff. Tracks its own cursor (not derivable from list length like the top
  // list's `reachedEnd`) because it pages by `since`, and `newlySince` pins which cutoff produced the current list.
  const [newlyAdded, setNewlyAdded] = useState<TTagUsingJob[]>([]);
  const [newlySince, setNewlySince] = useState(""); // cutoff that produced the current bottom list (used to paginate it)
  const [newlyCursor, setNewlyCursor] = useState<number | null>(null); // null = no more pages
  const [newlyLoading, setNewlyLoading] = useState(false);

  // One network op at a time across the whole modal (confirm + both lists' paging) — they share cutoff/cursor state.
  const busyRef = useRef(false);

  useEffect(() => {
    setJobs(initialJobs);
    setTotal(totalCount);
    setReachedEnd(initialJobs.length < PER_PAGE);
    setLoading(false);
    setCutoff(initialValidatedAt);
    setNewlyAdded([]);
    setNewlySince("");
    setNewlyCursor(null);
    setNewlyLoading(false);
    busyRef.current = false;
  }, [initialJobs, totalCount, tagId, initialValidatedAt]);

  // Run an async op exclusively: skip if one is already in flight, toggle its spinner, surface errors.
  const runExclusive = async (setSpinner: (v: boolean) => void, op: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSpinner(true);
    try {
      await op();
    } catch (e) {
      showError(e);
    } finally {
      busyRef.current = false;
      setSpinner(false);
    }
  };

  // Top list: `until` = initialValidatedAt (open time, not the advancing `cutoff`) keeps jobs tagged afterwards out.
  const loadMore = () => {
    if (reachedEnd) return;
    return runExclusive(setLoading, async () => {
      const res = await validateDeleteTagApi(entityType, tagId, jobs.at(-1)?.id, PER_PAGE, /* since */ undefined, initialValidatedAt);
      const more = res.data?.jobs ?? [];
      if (more.length) setJobs((prev) => [...prev, ...more]);
      if (res.data) setTotal(res.data.totalCount);
      setReachedEnd(more.length < PER_PAGE);
    });
  };

  // Delete with the current cutoff. If jobs were tagged since, the BE returns them → show below, advance the cutoff,
  // and let the user confirm again (loop converges once nothing new appears).
  const handleConfirm = () =>
    runExclusive(setLoading, async () => {
      const outcome = await onOk(cutoff);
      if (!outcome.success) {
        setNewlyAdded(outcome.jobs);
        setNewlySince(cutoff);
        setNewlyCursor(outcome.nextCursor);
        setCutoff(outcome.validatedAt);
      }
    });

  // Bottom list: paginate jobs tagged since `newlySince`, same keyset cursor as the top list.
  const loadMoreNewly = () => {
    if (newlyCursor == null) return;
    return runExclusive(setNewlyLoading, async () => {
      const res = await validateDeleteTagApi(entityType, tagId, newlyCursor, PER_PAGE, newlySince);
      const more = res.data?.jobs ?? [];
      if (more.length) setNewlyAdded((prev) => [...prev, ...more]);
      setNewlyCursor(res.data?.nextCursor ?? null);
    });
  };

  const handleJobClick = (jobId: number) => {
    window.open(getDynamicRoute(AppRoutes.jobDetail, { jobId: String(jobId) }), "_blank");
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      onOk={handleConfirm}
      labelClose="キャンセル"
      labelOk="削除する"
      width={500}
      enableFooter
      isLoadingOkBtn={isLoading || loading}
    >
      <Box mb={2}>
        {total > 0 ? (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body1">{TAG_DELETE_IN_USE_MESSAGE(tagLabel, total)}</Typography>
            <Typography variant="body1">{TAG_DELETE_UNASSIGN_MESSAGE}</Typography>
            <Typography variant="body1">{TAG_DELETE_CONFIRM_MESSAGE}</Typography>
          </Box>
        ) : (
          <Typography variant="body1" sx={{ mt: 1 }}>
            {TAG_DELETE_SIMPLE_CONFIRM_MESSAGE(tagLabel)}
          </Typography>
        )}
        {total > 0 && <JobScrollList jobs={jobs} loading={loading} onScroll={(e) => isNearBottom(e) && loadMore()} onJobClick={handleJobClick} />}

        {/* Warning placed outside the top scroll list (intentional). Paginates the same way (100/scroll). */}
        {newlyAdded.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body1" sx={{ color: orange[80], mb: 1, whiteSpace: "pre-line" }}>
              {TAG_DELETE_NEWLY_ADDED_MESSAGE}
            </Typography>
            <JobScrollList
              jobs={newlyAdded}
              loading={newlyLoading}
              onScroll={(e) => isNearBottom(e) && loadMoreNewly()}
              onJobClick={handleJobClick}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};
